import * as React from 'react';
import { Box, styled, Typography, TableHead } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableFooter from '@mui/material/TableFooter';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { getClientTableData } from '../../services/cloudFirestore';
import { UserContext } from '../../providers/UserProvider';
import { TablePaginationActions } from './TablePaginationActions';
import { StyledDashboardHeading } from '../../theme/StyledElements';
import { ClientTableData } from '../../models/ClientTableData';
import { useMountedRef } from '../../hooks/useMountedRef';

const StyledTableCell = styled(TableCell)(() => ({
    width: 160,
    color: '#FFFFFF'
}));

const StyledTableTypography = styled(Typography)(() => ({
    color: '#134C03',
    textTransform: 'none',
    letterSpacing: 0,
    fontWeight: 500
}));

export function ClientTable(): React.ReactElement {
    const { t } = useTranslation();
    const mounted = useMountedRef();
    const user = useContext(UserContext);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [orderData, setOrderData] = useState<ClientTableData[]>([]);

    useEffect(() => {
        if (user && user.idProducteur && mounted.current) {
            getClientTableData(user.idProducteur).then((data) => {
                if (mounted.current) {
                    setOrderData(data);
                }
            });
        }
    }, []);

    const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - orderData.length) : 0;

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number
    ): void => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
    ): void => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <>
            <Box>
                <StyledDashboardHeading variant="h3" sx={{ margin: '40px 0px 20px 0px' }}>
                    {t('myClients')}
                </StyledDashboardHeading>
                <TableContainer component={Paper} sx={{ width: '880px' }}>
                    <Table
                        sx={{ minWidth: 500, color: '#134C03' }}
                        aria-label="custom pagination table">
                        <TableHead sx={{ backgroundColor: '#134C03' }}>
                            <TableRow>
                                <StyledTableCell>{t('client(s)')}</StyledTableCell>
                                <StyledTableCell>{t('customerService')}</StyledTableCell>
                                <StyledTableCell>{t('totalTurnOver')}</StyledTableCell>
                                <StyledTableCell>{t('turnOverPerMonth')}</StyledTableCell>
                                <StyledTableCell>{t('visitsPerMonth')}</StyledTableCell>
                                <StyledTableCell>{t('satisfaction')}</StyledTableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(rowsPerPage > 0
                                ? orderData.slice(
                                      page * rowsPerPage,
                                      page * rowsPerPage + rowsPerPage
                                  )
                                : orderData
                            ).map((row) => (
                                <TableRow key={row.clientName}>
                                    <TableCell style={{ width: 160 }}>
                                        <StyledTableTypography variant="overline">
                                            {row.clientName}
                                        </StyledTableTypography>
                                    </TableCell>
                                    <TableCell style={{ width: 160 }}>
                                        <StyledTableTypography variant="overline">
                                            {row.orderDate}
                                        </StyledTableTypography>
                                    </TableCell>
                                    <TableCell style={{ width: 160 }}>
                                        <StyledTableTypography variant="overline">
                                            {row.totalAmount?.toFixed(2)}€
                                        </StyledTableTypography>
                                    </TableCell>
                                    <TableCell style={{ width: 160 }}>
                                        <StyledTableTypography variant="overline">
                                            {row.averageAmount?.toFixed(2)}€
                                        </StyledTableTypography>
                                    </TableCell>
                                    <TableCell style={{ width: 160 }}>
                                        <StyledTableTypography variant="overline">
                                            {row.numberOfOrders}
                                        </StyledTableTypography>
                                    </TableCell>
                                    <TableCell style={{ width: 160 }}>
                                        <StyledTableTypography variant="overline">
                                            {row.satisfaction}
                                        </StyledTableTypography>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {emptyRows > 0 && (
                                <TableRow style={{ color: '#134C03', height: 53 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                        <TableFooter>
                            <TableRow>
                                <TablePagination
                                    sx={{
                                        color: '#134C03',
                                        width: '100%'
                                    }}
                                    rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                                    colSpan={7}
                                    count={orderData.length}
                                    rowsPerPage={rowsPerPage}
                                    page={page}
                                    SelectProps={{
                                        inputProps: {
                                            'aria-label': 'Vue par page',
                                            color: '#134C03'
                                        },
                                        native: true
                                    }}
                                    onPageChange={handleChangePage}
                                    onRowsPerPageChange={handleChangeRowsPerPage}
                                    ActionsComponent={TablePaginationActions}
                                    labelRowsPerPage={t('rowsPerPage')}
                                    labelDisplayedRows={({ from, to, count }): string => {
                                        return `${from}–${to} sur ${
                                            count !== -1 ? count : `more than ${to}`
                                        }`;
                                    }}
                                />
                            </TableRow>
                        </TableFooter>
                    </Table>
                </TableContainer>
            </Box>
        </>
    );
}
